import { Button, ButtonProps } from '@mui/material';
// eslint-disable-next-line no-restricted-imports
import { t } from 'i18next'; // We can disable eslint for this line, it's ok here.
import { enqueueSnackbar, OptionsObject, useSnackbar } from 'notistack';

import { RequestError } from '../types/request-error.types';

const globalConfig = {
  autoHideDuration: 5000,
  preventDuplicate: true,
};

export const createNotificationAction = (title: string, props?: ButtonProps) => <Button {...props}>{title}</Button>;

/**
 * Access notifications without hooks
 */

export const notification = {
  showSuccessNotification(message: string, config: OptionsObject = {}) {
    enqueueSnackbar(message, {
      variant: 'success',
      ...globalConfig,
      ...config,
    });
  },

  showErrorNotification(
    {
      message,
      error,
    }: {
      message?: string;
      // error is required so caseId is not forgotten. Pass null if you don't need it.
      error: RequestError | Error | null;
    },
    config: OptionsObject = {},
  ) {
    const errorData = error && 'data' in error && error.data ? error.data.data : undefined;
    const errorMessage = message ?? errorData?.message ?? errorData?.msg ?? t('common.general-error');
    const caseId = errorData?.caseId;

    enqueueSnackbar(
      <div className="notistack-message">
        {errorMessage}
        {caseId && <div className="notistack-case-id">{caseId}</div>}
      </div>,
      {
        variant: 'error',
        ...globalConfig,
        // Use a string key based on the error message for duplicate detection
        key: errorMessage + (caseId || ''),
        ...config,
      },
    );
  },

  showWarningNotification(message: string, config: OptionsObject = {}) {
    enqueueSnackbar(message, {
      variant: 'warning',
      ...globalConfig,
      ...config,
    });
  },

  showInfoNotification(message: string, config: OptionsObject = {}) {
    enqueueSnackbar(message, {
      variant: 'info',
      ...globalConfig,
      ...config,
    });
  },
};

/**
 * Adds some functionality to notistack's snackbar
 */
export function useNotification() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const showSuccessNotification = (message: string, config: OptionsObject = {}) =>
    enqueueSnackbar(message, {
      variant: 'success',
      ...globalConfig,
      ...config,
    });

  const showErrorNotification = notification.showErrorNotification;

  const showWarningNotification = (message: string, config: OptionsObject = {}) =>
    enqueueSnackbar(message, {
      variant: 'warning',
      ...globalConfig,
      ...config,
    });

  const showInfoNotification = (message: string, config: OptionsObject = {}) =>
    enqueueSnackbar(message, { variant: 'info', ...globalConfig, ...config });

  const closeNotifications = closeSnackbar;

  return {
    ...useSnackbar(),
    showSuccessNotification,
    showErrorNotification,
    showWarningNotification,
    showInfoNotification,
    closeNotifications,
  };
}
